/**
 * Filter system models for the new API
 * Based on the comprehensive filtration system documentation
 */

import { Geometry } from 'geojson';

// Standard filter operators
export enum FilterOperator {
  EQ = 'eq',
  NE = 'ne',
  GT = 'gt',
  GTE = 'gte',
  LT = 'lt',
  LTE = 'lte',
  LIKE = 'like',
  ILIKE = 'ilike',
  IN = 'in',
  NOT_IN = 'not_in',
  IS_NULL = 'is_null',
  IS_NOT_NULL = 'is_not_null',
  CONTAINS = 'contains',
  EXISTS = 'exists',
}

// Spatial operators (PostGIS)
export enum SpatialOperator {
  ST_INTERSECTS = 'st_intersects',
  ST_CONTAINS = 'st_contains',
  ST_WITHIN = 'st_within',
  ST_DWITHIN = 'st_dwithin',
  ST_OVERLAPS = 'st_overlaps',
  ST_TOUCHES = 'st_touches',
  ST_CROSSES = 'st_crosses',
}

// Logical operators
export enum LogicalOperator {
  AND = 'and',
  OR = 'or',
}

// Sort directions
export enum SortDirection {
  ASC = 'asc',
  DESC = 'desc',
}

// Basic filter field
export interface FilterField {
  field: string;
  operator: FilterOperator | SpatialOperator;
  value: any;
}

// Filter group for complex logical operations
export interface FilterGroup {
  operator: LogicalOperator;
  conditions: (FilterField | FilterGroup)[];
}

// Sort configuration
export interface SortConfig {
  field: string;
  direction: SortDirection;
}

// Pagination configuration
export interface PaginationConfig {
  skip?: number;
  limit?: number;
  cursor?: string;
}

// Spatial filter value for distance-based queries
export interface SpatialDistanceValue {
  geometry: Geometry;
  distance: number;
  unit: 'meters' | 'kilometers' | 'feet' | 'miles';
}

// Query configuration
export interface QueryConfig {
  filters?: FilterField[] | FilterGroup;
  sorts?: SortConfig[];
  pagination?: PaginationConfig;
  scopes?: ScopeConfig[];
}

// Scope configuration for hierarchical filtering
export interface ScopeConfig {
  scope_type: 'project' | 'folder';
  project_scope?: {
    project_id: string;
  };
  folder_scope?: {
    folder_id: string;
    include_subfolders?: boolean;
  };
  area_filters?: {
    filters?: string;
    include_ids?: string[];
    exclude_ids?: string[];
  };
}

// API request parameters
export interface ApiRequestParams {
  filters?: string;
  sorts?: string;
  skip?: number;
  limit?: number;
  cursor?: string;
  scopes?: string;
}

// Common filter builders
export interface TextFilterBuilder {
  equals(value: string): FilterField;
  contains(value: string): FilterField;
  startsWith(value: string): FilterField;
  endsWith(value: string): FilterField;
  like(value: string): FilterField;
  ilike(value: string): FilterField;
  in(values: string[]): FilterField;
  notIn(values: string[]): FilterField;
  isNull(): FilterField;
  isNotNull(): FilterField;
}

export interface NumericFilterBuilder {
  equals(value: number): FilterField;
  greaterThan(value: number): FilterField;
  greaterThanOrEqual(value: number): FilterField;
  lessThan(value: number): FilterField;
  lessThanOrEqual(value: number): FilterField;
  in(values: number[]): FilterField;
  notIn(values: number[]): FilterField;
  between(min: number, max: number): FilterField[];
}

export interface DateFilterBuilder {
  equals(value: Date | string): FilterField;
  after(value: Date | string): FilterField;
  before(value: Date | string): FilterField;
  between(start: Date | string, end: Date | string): FilterField[];
  isNull(): FilterField;
  isNotNull(): FilterField;
}

export interface BooleanFilterBuilder {
  equals(value: boolean): FilterField;
  isTrue(): FilterField;
  isFalse(): FilterField;
}

export interface SpatialFilterBuilder {
  intersects(geometry: Geometry): FilterField;
  contains(geometry: Geometry): FilterField;
  within(geometry: Geometry): FilterField;
  withinDistance(
    geometry: Geometry,
    distance: number,
    unit?: 'meters' | 'kilometers' | 'feet' | 'miles'
  ): FilterField;
  overlaps(geometry: Geometry): FilterField;
  touches(geometry: Geometry): FilterField;
  crosses(geometry: Geometry): FilterField;
}

export interface JsonFilterBuilder {
  path(path: string): JsonFilterBuilder;
  equals(value: any): FilterField;
  contains(value: any): FilterField;
  exists(): FilterField;
  isNull(): FilterField;
  isNotNull(): FilterField;
  greaterThan(value: number): FilterField;
  lessThan(value: number): FilterField;
  in(values: any[]): FilterField;
}

// Utility types
export type FilterBuilder =
  | TextFilterBuilder
  | NumericFilterBuilder
  | DateFilterBuilder
  | BooleanFilterBuilder
  | SpatialFilterBuilder
  | JsonFilterBuilder;

// Common field types for type safety
export type CommonFields =
  | 'id'
  | 'name'
  | 'created_at'
  | 'updated_at'
  | 'area_sq_meters'
  | 'geometry'
  | 'properties'
  | 'project_id'
  | 'folder_id'
  | 'tenant_id'
  | 'team_id'
  | 'user_id';

// Predefined filter sets for common use cases
export interface CommonFilters {
  // Text filters
  name: TextFilterBuilder;
  description: TextFilterBuilder;

  // Numeric filters
  area_sq_meters: NumericFilterBuilder;
  id: NumericFilterBuilder;

  // Date filters
  created_at: DateFilterBuilder;
  updated_at: DateFilterBuilder;

  // Boolean filters
  is_active: BooleanFilterBuilder;
  is_archived: BooleanFilterBuilder;

  // Spatial filters
  geometry: SpatialFilterBuilder;

  // JSON filters
  properties: JsonFilterBuilder;
  metadata: JsonFilterBuilder;
}
